import { tz } from "@date-fns/tz";
import {
  AnalyticEvent,
  extrudeAdditionalInfo,
  extrudeKeyInfo,
  formatDurationToDays,
  formatPrice,
  formatReservationDate,
  isNative,
  isWeb,
  useCheckoutPaymentVerificationMutation,
  useConfirmCheckoutMutation,
  usePostHog,
  useToday,
} from "@stepapp/core";
import { CategoryType, Order_Discount_Type } from "@stepapp/core/apiTypes";
import { captureSentryException } from "@stepapp/sentry";
import { PaymentSheetError } from "@stepapp/stripe/src/types";
import {
  Block,
  Button,
  Divider,
  Input,
  LinePlaceholder,
  Modal,
  OrderInfoItem,
  palette,
  Snackbar,
  SvgIcon,
  spacing,
  Typography,
  useBreakpoints,
  useHeaderHeight,
} from "@stepapp/ui";
import { addDays, differenceInHours, isAfter, sub } from "date-fns";
import * as Linking from "expo-linking";
import { useLocalSearchParams, useRouter } from "expo-router";
import * as WebBrowser from "expo-web-browser";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  KeyboardAvoidingView,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  ScrollView,
  StyleSheet,
  type TextInput,
  useWindowDimensions,
} from "react-native";
import {
  AverageCleaningTime,
  CancellationDetails,
  CouponModal,
  LoginForm,
  ReservationNote,
  ServiceSceneDescription,
  TotalAndCoupon,
} from "../../../src/components";
import { ServiceCheckout } from "../../../src/components/Service/ServiceCheckout";
import { APP_URL } from "../../../src/config/urls";
import { focusInput } from "../../../src/helpers";
import {
  useAppSelector,
  useCheckout,
  useCreateOrUpdateAddress,
  useGetServiceSummaryBasicAnswers,
  useRedeemCoupon,
} from "../../../src/hooks";
import { useSendGTMData } from "../../../src/hooks/useSendGTMData";
import { AppStorage } from "../../../src/services/AppStorage";
import type { ReservationFormParams } from "../../../src/store/draftReservation/types";

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: skip
const ServiceSummaryScreen = () => {
  const { t } = useTranslation();
  const today = useToday();
  const postHog = usePostHog();
  const sendGTMData = useSendGTMData();
  const { navigate, replace } = useRouter();
  const { slug } = useLocalSearchParams();
  const { mobileOnly } = useBreakpoints();
  const { width: screenWidth, height: screenHeight } = useWindowDimensions();
  const priceFormatter = formatPrice(AppStorage().get("countryCode"));

  const configTimezone = useAppSelector(({ appConfig }) => appConfig.timezone);
  const userId = useAppSelector(({ user }) => user.id);
  const properties = useAppSelector(({ user }) => user.properties);

  const { checkout, loading: checkoutLoading } = useCheckout({ skipValidation: false });

  const currentDescription = extrudeAdditionalInfo(checkout?.address?.notes || "");
  const keyInfo = extrudeKeyInfo(checkout?.address?.notes);
  const willCustomerBePresent = keyInfo === t("screens.serviceSummary.iWillBeThere");

  const loginRef = useRef<TextInput>(null);
  const scrollViewRef = useRef<ScrollView>(null);
  const [loginInputPosition, setLoginInputPosition] = useState({ x: 0, y: 0 });
  const [loading, setLoading] = useState(false);
  const [couponVisible, setCouponVisible] = useState(false);
  const [showNotAvailableDateModal, setShowNotAvailableDateModal] = useState(false);
  const [showScrollToLoginButton, setShowScrollToLoginButton] = useState(true);
  const [tempDescription, setTempDescription] = useState(currentDescription || "");
  const [successSnackbarVisible, setSuccessSnackbarVisible] = useState(false);
  const [errorSnackbarVisible, setErrorSnackbarVisible] = useState(false);

  const { handleSubmit, watch, setValue } = useFormContext<ReservationFormParams>();
  const { paymentMethodId, locationId, couponCode } = watch();

  const property = properties?.find((property) => property.propertyId === checkout?.propertyId);
  const [createOrUpdateAddress] = useCreateOrUpdateAddress({
    property,
    isUserLogged: !!userId,
    localizationId: locationId,
  });

  const { redeemCoupon, loadingCoupon, title, description } = useRedeemCoupon({
    onSuccess: () => {
      setSuccessSnackbarVisible(true);
    },
    onError: () => {
      setErrorSnackbarVisible(true);
    },
  });

  const coupon = checkout?.discounts?.find((d) => Order_Discount_Type.Coupon === d?.type);
  const basicAnswers = useGetServiceSummaryBasicAnswers({ checkout });
  const [paymentVerification] = useCheckoutPaymentVerificationMutation();
  const [confirmCheckout, { loading: confirmCheckoutLoading }] = useConfirmCheckoutMutation();
  const isSubscription = !!formatDurationToDays(checkout?.periodicity?.cycleDuration || "P0D");

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: skip
  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const handleSubmitReservation = useCallback(async () => {
    const selectedDate = checkout?.startDate ? new Date(checkout?.startDate).toISOString() : "";
    const isAfterTomorrow = isAfter(selectedDate, addDays(today, 1));

    if (!isAfterTomorrow) {
      setShowNotAvailableDateModal(true);
      return;
    }

    setLoading(true);

    coupon && postHog?.capture(AnalyticEvent.Reservation.CouponUsed, { $set: { coupon } });

    if (checkout?.paymentMethodId) {
      const returnUrl = isNative
        ? `${APP_URL}/dismiss-browser?returnUrl=${Linking.createURL(`/service/${slug}/summary`)}`
        : `${APP_URL}/service/${slug}/3DS-authentication-complete?checkoutId=${checkout.id}`;

      const { data, errors } = await paymentVerification({
        variables: {
          input: {
            checkoutId: checkout?.id,
            returnUrl,
          },
        },
      });

      if (errors) {
        setLoading(false);
        captureSentryException("[Checkout error]: paymentVerification error", {
          extra: {
            userId,
            checkoutId: checkout?.id,
            errorMessage: errors[0]?.message,
          },
        });
        return alert(errors[0]?.message);
      }

      const requireVerification =
        data?.checkoutPaymentVerification.requiresAdditionalActions &&
        !data?.checkoutPaymentVerification.confirmed;

      if (requireVerification) {
        handlePaymentVerification(data?.checkoutPaymentVerification.nextStep?.redirectUrl || "");
        setLoading(false);
        return;
      }

      handleConfirmCheckout();
    }
    setLoading(false);
  }, [
    userId,
    checkout?.startDate,
    checkout?.paymentMethodId,
    checkout?.id,
    today,
    postHog,
    coupon,
    paymentVerification,
    slug,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  const handleConfirmCheckout = useCallback(async () => {
    let orderId = "";
    const { data, errors } = await confirmCheckout({
      variables: { checkoutId: checkout?.id || "" },
    });

    setLoading(false);
    orderId = data?.confirmCheckout?.id || "";

    if (errors) {
      const retryConfirm = await confirmCheckout({ variables: { checkoutId: checkout?.id || "" } });

      const errorMessage = retryConfirm?.errors?.[0]?.message;

      orderId = retryConfirm?.data?.confirmCheckout?.id || "";

      if (errorMessage && errorMessage !== PaymentSheetError.Canceled) {
        postHog?.capture(AnalyticEvent.Payment.Failure, {
          errorMessage,
          retryConfirm: JSON.parse(
            JSON.stringify({
              data: retryConfirm?.data,
              errors: retryConfirm?.errors?.map((error) => ({
                message: error.message,
                extensions: error.extensions,
              })),
            }),
          ),
        });
        captureSentryException("[Checkout error]: confirmCheckout error", {
          extra: {
            userId,
            checkoutId: checkout?.id,
            errorMessage,
          },
        });
        alert(t("alerts.paymentFailed"));
        return;
      }
    }

    const eventType = isSubscription ? AnalyticEvent.GTM.Subscription : AnalyticEvent.GTM.Purchase;
    sendGTMData({ checkout, eventType });

    replace(`/service/${slug}/confirmation?orderId=${orderId}`);
  }, [sendGTMData, confirmCheckout, checkout?.id, postHog?.capture, replace, slug]);

  const handleDescriptionUpdate = useCallback(async () => {
    const newValues = {
      ...property,
      propertyId: property?.propertyId,
      notes: `${tempDescription}\n\n${keyInfo || ""}`,
    };
    setValue("address.notes", `${tempDescription}\n\n${keyInfo || ""}`);
    if (property?.propertyId) {
      await createOrUpdateAddress(newValues);
    }
  }, [createOrUpdateAddress, keyInfo, property, tempDescription, setValue]);

  const handlePaymentVerification = async (targetUrl: string) => {
    const res = await WebBrowser.openAuthSessionAsync(targetUrl, null, {
      preferEphemeralSession: true,
    });

    if (res?.type === "success" && res.url && isNative) {
      setLoading(true);

      setTimeout(() => {
        handleConfirmCheckout();
      }, 1000);
    }
  };

  const navigateToQuestionnaireEdit = useCallback(
    (editQuestionId: string) => {
      if (editQuestionId === "home") {
        const hasProperty = property?.propertyId ? "pick-property" : "add-address";
        const platformPath = isWeb ? "configure" : hasProperty;

        navigate(`/service/${slug}/${platformPath}?editId=${editQuestionId}`);
        return;
      }
      const platformPath = isWeb ? "configure" : `question_${editQuestionId}`;

      navigate(`/service/${slug}/${platformPath}?editId=${editQuestionId}`);
    },
    [navigate, slug, property?.propertyId],
  );

  const handleEditNote = useCallback(() => {
    const platformPath = isWeb ? "configure" : "notes";

    navigate(`/service/${slug}/${platformPath}?editId=notes`);
  }, [navigate, slug]);

  const navigateToDateEdit = useCallback(() => {
    const platformPath = isWeb ? "date-and-address" : "date";

    navigate(`/service/${slug}/${platformPath}?editId=date`);
  }, [navigate, slug]);

  const navigateToAddressEdit = useCallback(() => {
    const hasProperty = property?.propertyId ? "pick-property" : "add-address";
    const platformPath = isWeb ? "date-and-address" : hasProperty;

    navigate(`/service/${slug}/${platformPath}?editId=address`);
  }, [navigate, slug, property?.propertyId]);

  const selectPaymentMethod = useCallback(
    (id: string) => {
      setValue("paymentMethodId", id);
    },
    [setValue],
  );

  const handleCouponCode = useCallback(
    (coupon: string) => {
      setValue("couponCode", coupon);
    },
    [setValue],
  );

  const handleLayout = useCallback(() => {
    loginRef.current?.measureLayout(
      // @ts-expect-error
      scrollViewRef?.current,
      (x, y) => {
        setLoginInputPosition({ x, y: y + 200 });
      },
      () => {},
    );
  }, []);

  const handleScrollToInput = useCallback(() => {
    scrollViewRef?.current?.scrollTo({ y: loginInputPosition.y - 200, animated: true });
    loginRef.current?.focus();
  }, [loginInputPosition]);

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { y } = event.nativeEvent?.contentOffset || { y: 0 };
      if (y + screenHeight > loginInputPosition.y) {
        setShowScrollToLoginButton(false);
      } else {
        setShowScrollToLoginButton(true);
      }
    },
    [loginInputPosition.y, screenHeight],
  );

  const requirePresenceInfo = checkout?.bundle?.categoryType !== CategoryType.Massage;
  const isSubmitDisabled = !paymentMethodId || confirmCheckoutLoading || loading;
  const isSubmitLoading = loading || checkoutLoading || confirmCheckoutLoading;
  const showPlaceholder = checkoutLoading && !checkout?.id;

  const reservationDate = useMemo(
    () => (checkout?.startDate ? new Date(checkout?.startDate).toISOString() : ""),
    [checkout?.startDate],
  );
  const instantPayment = useMemo(
    () =>
      differenceInHours(checkout?.startDate || "", new Date(), { in: tz(configTimezone) }) <= 48,
    [checkout?.startDate, configTimezone],
  );

  // TODO invoice form
  // const handleShowInvoice = useCallback(() => {
  //   const newValue = !meta?.addInvoice;
  //   if (!newValue) {
  //     setValue("billingAddress", null);
  //     clearErrors();
  //   }
  //   setValue("meta.addInvoice", newValue);
  // }, [meta?.addInvoice, setValue, clearErrors]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    sendGTMData({ checkout, eventType: AnalyticEvent.GTM.InitiateCheckout });
  }, [userId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: skip
  useEffect(() => {
    if (isNative) return;

    const handleRetrySubmit = async (e: ToDo) => {
      if (e?.data !== "3DS-authentication-complete") return;
      setLoading(true);
      window?.postMessage("submitted");

      const { data } = await paymentVerification({
        variables: { input: { checkoutId: checkout?.id || "", returnUrl: APP_URL } },
      });

      const isConfirmed = !!data?.checkoutPaymentVerification.confirmed;

      isConfirmed ? handleConfirmCheckout() : setLoading(false);
    };

    window?.addEventListener("message", handleRetrySubmit);
    return () => window?.removeEventListener("message", handleRetrySubmit);
  }, [checkout?.id]);

  return (
    <>
      <KeyboardAvoidingView
        behavior="height"
        keyboardVerticalOffset={useHeaderHeight() + spacing.unit * 8}
      >
        <ScrollView
          keyboardShouldPersistTaps="always"
          onLayout={handleLayout}
          onScroll={handleScroll}
          ref={scrollViewRef}
          scrollEventThrottle={16}
        >
          <Block marginTop={isWeb ? 5 : 2}>
            <Typography variant="newH2">{t("pages.serviceSummary.title")}</Typography>
          </Block>

          <ServiceSceneDescription description={t("pages.serviceSummary.description")} />

          <Block marginTop={2}>
            <Block marginBottom={3}>
              <ServiceSceneDescription description={t("common.service")} />

              {showPlaceholder ? (
                <>
                  <LinePlaceholder height={3} width={20} />
                  <Block marginTop={3}>
                    <LinePlaceholder borderRadius={1} height={10} width={(screenWidth - 40) / 8} />
                    <LinePlaceholder
                      marginTop={1}
                      borderRadius={1}
                      height={10}
                      width={(screenWidth - 40) / 8}
                    />
                  </Block>
                </>
              ) : (
                <Typography variant="newH3">{checkout?.bundle?.name}</Typography>
              )}
            </Block>

            {basicAnswers?.map(({ id, questionTitle, answerPrice, answerTitle }) => (
              <OrderInfoItem
                answer={answerTitle}
                key={`${id}-${answerTitle}`}
                onEdit={() => navigateToQuestionnaireEdit(id)}
                price={answerPrice}
                currency={checkout?.currency}
                title={questionTitle}
              />
            ))}
          </Block>

          {checkout?.services && checkout?.services?.length > 0 && (
            <Block marginTop={1.5}>
              <Block marginBottom={1.5}>
                <Typography variant="bodyBold">
                  {t("pages.serviceSummary.additionalServices")}
                </Typography>
              </Block>

              {checkout?.services?.map((serviceValue) => (
                <OrderInfoItem
                  additionalServices={serviceValue?.variants}
                  description={priceFormatter(serviceValue?.price, {
                    currency: serviceValue?.currency,
                  })}
                  currency={serviceValue?.currency}
                  key={`${serviceValue?.service?.id}-${serviceValue?.service?.name}`}
                  onEdit={() => navigateToQuestionnaireEdit("services")}
                  title={serviceValue?.service?.name}
                />
              ))}
            </Block>
          )}

          <Block marginVertical={1.5}>
            <Block marginBottom={1.5}>
              <Typography variant="bodyBold">{t("pages.serviceSummary.startDate")}</Typography>
            </Block>

            <OrderInfoItem
              loading={showPlaceholder}
              reverseStyles
              answer={formatReservationDate(reservationDate, "HH:mm")}
              onEdit={navigateToDateEdit}
              title={formatReservationDate(reservationDate, "EEEE, d.MM.yyyy")}
            />
          </Block>

          <Divider color={palette.secondary.grey} />

          <ReservationNote
            note={checkout?.customerNote}
            onPress={handleEditNote}
            containerStyles={styles.customerNote}
            categoryType={checkout?.bundle?.categoryType}
          />

          <Block marginTop={3}>
            <Block marginBottom={1.5}>
              <Typography variant="bodyBold">{t("screens.serviceSummary.orderingFor")}</Typography>
              <Typography type="secondary" variant={mobileOnly ? "lightLabel" : "paragraph"}>
                {t("screens.serviceSummary.orderingForDescription")}
              </Typography>
            </Block>

            <OrderInfoItem
              loading={showPlaceholder}
              homeSize={checkout?.address?.floorArea || 0}
              homeType={checkout?.address?.type}
              onEdit={navigateToAddressEdit}
              title={
                checkout?.address
                  ? `${checkout?.address?.streetAddress1} ${
                      checkout?.address?.streetAddress2 || ""
                    }`
                  : ""
              }
              titleContinue={
                checkout?.address
                  ? `${checkout?.address?.postalOrZipCode} ${checkout?.address?.city}`
                  : ""
              }
              type="address"
            />
          </Block>

          {requirePresenceInfo && (
            <Block marginVertical={1.5}>
              <Block marginBottom={1.5}>
                <Typography variant="bodyBold">
                  {t("pages.serviceSummary.howAreWeGoingToEnter")}
                </Typography>
              </Block>

              <OrderInfoItem
                loading={showPlaceholder}
                reverseStyles
                answer={willCustomerBePresent ? undefined : keyInfo}
                onEdit={navigateToAddressEdit}
                title={
                  willCustomerBePresent
                    ? keyInfo
                    : t("pages.serviceDateAndAddress.iWillLeaveTheKey")
                }
              />
            </Block>
          )}

          <Divider color={palette.secondary.grey} />

          <Block marginBottom={1} marginTop={3}>
            <Block marginBottom={1.5}>
              <Typography variant="bodyBold">
                {t("pages.serviceDateAndAddress.instructionsForArrival")}
              </Typography>
            </Block>
            <Input
              multiline
              onBlur={handleDescriptionUpdate}
              onChangeText={setTempDescription}
              placeholder={t("components.input.placeholder.additionalInstructions")}
              value={tempDescription}
            />
          </Block>

          <CancellationDetails
            date={formatReservationDate(sub(reservationDate, { days: 2 }), "d MMMM HH:mm")}
            instantPayment={instantPayment}
            categoryType={checkout?.bundle?.categoryType}
          />

          <Divider color={palette.secondary.grey} />

          <AverageCleaningTime
            loading={showPlaceholder}
            showDescription
            marginVertical={3}
            timeConsumption={checkout?.timeConsumption}
            categoryType={checkout?.bundle?.categoryType}
          />

          <Divider color={palette.secondary.grey} />

          <TotalAndCoupon
            couponName={checkout?.couponCode || ""}
            currency={checkout?.currency}
            discounts={checkout?.discounts}
            handleFocusInput={focusInput(loginRef)}
            isLogged={!!userId}
            loading={showPlaceholder}
            onCouponPress={() => setCouponVisible(true)}
            price={checkout?.priceAfterDiscounts || 0}
            total={checkout?.totalPrice || 0}
          />

          {/* // todo invoice form */}
          {/* <Divider color={palette.secondary.grey} />

          <Block marginVertical={2}>
            <Typography variant="newH3">{t("pages.serviceSummary.VATInvoice")}</Typography>

            <Block row marginTop={1.5}>
              <RadioButton onValueChange={() => handleShowInvoice()} value={meta?.addInvoice} />
              <Block justifyCenter marginLeft={2}>
                <Typography variant="body">
                  {t("pages.serviceSummary.wantToGetVATInvoice")}
                </Typography>
              </Block>
            </Block>

            {meta?.addInvoice && (
              <>
                <Block marginBottom={3}>
                  <ServiceSceneDescription
                    description={t("pages.serviceSummary.invoiceBeforeSubmit")}
                  />
                </Block>

                <VATInvoiceForm
                  city={billingAddress?.city || ""}
                  email={billingAddress?.email || ""}
                  errors={errors}
                  line1={billingAddress?.line1 || ""}
                  line2={billingAddress?.line2 || ""}
                  name={billingAddress?.name || ""}
                  postal_code={billingAddress?.postal_code || ""}
                  setValue={setValue}
                  taxIdentification={billingAddress?.taxIdentification || ""}
                  trigger={trigger}
                />
                <Typography type="secondary" variant="lightLabel">
                  {t("pages.serviceSummary.invoiceAdditionalEmail")}
                </Typography>
              </>
            )}
          </Block> */}

          <Divider color={palette.secondary.grey} />

          <Block marginTop={2} paddingBottom={isWeb ? 5 : 15}>
            {userId ? (
              <ServiceCheckout
                selectPaymentMethod={selectPaymentMethod}
                paymentMethodId={paymentMethodId || ""}
                handleCheckout={handleSubmit(handleSubmitReservation)}
                disabledSubmit={isSubmitDisabled}
                loadingSubmit={isSubmitLoading}
              />
            ) : (
              <LoginForm ref={loginRef} />
            )}
          </Block>
        </ScrollView>
      </KeyboardAvoidingView>

      {userId && isNative ? (
        <Block paddingBottom={0} paddingTop={2} style={styles.submitButton}>
          <Button
            disabled={isSubmitDisabled}
            label={t("screens.serviceSummary.confirmAndOrder")}
            loading={isSubmitLoading}
            onPress={handleSubmit(handleSubmitReservation)}
            type="primary"
            testID="submitOrder"
          />
        </Block>
      ) : (
        isNative &&
        showScrollToLoginButton && (
          <Button
            label={t("screens.serviceSummary.logIn")}
            onPress={() => handleScrollToInput()}
            rightComponent={() => (
              <Block marginLeft={1} marginRight={-1}>
                <SvgIcon color={palette.common.white} name="chevron-down" size={26} />
              </Block>
            )}
            style={{
              marginHorizontal: screenWidth / 3.5,
              bottom: spacing.unit * 12,
            }}
            type="primary"
          />
        )
      )}

      <Snackbar
        isVisible={!isWeb && successSnackbarVisible}
        setIsVisible={setSuccessSnackbarVisible}
        text={title}
      />

      <CouponModal
        description={description}
        errorSnackbarVisible={errorSnackbarVisible}
        isVisible={couponVisible}
        loadingCoupon={loadingCoupon}
        redeemCoupon={redeemCoupon}
        selectedCoupon={couponCode || ""}
        setCoupon={handleCouponCode}
        setErrorSnackbarVisible={setErrorSnackbarVisible}
        setIsVisible={setCouponVisible}
        setSuccessSnackbarVisible={setSuccessSnackbarVisible}
        successSnackbarVisible={successSnackbarVisible}
        title={title}
      />

      <Modal
        primaryAction={() => setShowNotAvailableDateModal(false)}
        primaryActionLabel={t("common.OK")}
        title={t("pages.serviceSummary.dateNotAvailable")}
        toggle={() => setShowNotAvailableDateModal(false)}
        visible={showNotAvailableDateModal}
      />
    </>
  );
};

export default memo(ServiceSummaryScreen);

const styles = StyleSheet.create({
  customerNote: {
    paddingTop: spacing.unit * 3,
  },
  submitButton: {
    backgroundColor: palette.common.white,
    borderTopColor: palette.common.lightGray,
    borderTopWidth: spacing.border,
    margin: "auto",
    bottom: 0,
    paddingBottom: spacing.unit * 2,
    position: isWeb ? "relative" : "absolute",
    right: 0,
    left: 0,
    width: isWeb ? "100%" : spacing.deviceWidth,
    paddingHorizontal: spacing.unit * (isWeb ? 0 : 2.5),
    zIndex: 2,
  },
});
